<template>
  <div class="flex w-full flex-1 flex-col items-center px-6">
    <div class="flex w-full max-w-[450px] flex-grow flex-col">
      <div class="my-auto text-center">
        <h1
          class="text-[28px] font-medium leading-[34px] tablet-h:text-[32px] tablet-h:leading-[38px]"
        >
          <span class="text-gray-400">Tailored to</span>
          <span class="text-gray-800 ml-1">your work.</span>
        </h1>

        <div
          class="mt-[30px] flex flex-col gap-[24px] font-normal text-[16px] leading-[22px] text-gray-800"
        >
          <p>
            You create
            <span class="font-medium italic">
              {{ creations }}, {{ frequency }}</span
            >
          </p>
          <p>
            Your work is
            <span class="font-medium italic">{{ income }}</span>
          </p>
          <p>
            You want
            <span class="font-medium italic">{{ alerts }}</span>
            when it’s used
          </p>
          <p>
            You need
            <span class="font-medium italic">{{ protection }}</span>
            protection
          </p>
        </div>
      </div>

      <Button
        class="w-full"
        variant="primary"
        size="lg"
        :loading="loading"
        @click="handleClick"
      >
        {{ buttonLabel }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { Button } from '@dacr/library'

import {
  MembershipTier,
  PlanType,
  useCompleteOnboardingMutation,
  useCompleteStepMutation,
} from '@/shared/graphql/legacy/generated'
import { OnboardingStep, useOnboardingStore } from '@/features/onboarding/store'
import {
  clearPricingCheckout,
  pricingCheckout,
} from '@/features/onboarding/pricingCheckout'
import { useBillingStore } from '@/features/billing/store'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { analytics } from '@/shared/services/analytics'
import { openModal, Modals } from '@/shared/dialogs/modals'

const { mutate: completeStep } = useCompleteStepMutation()
const { mutate: completeOnboarding } = useCompleteOnboardingMutation()

const router = useRouter()
const onboardingStore = useOnboardingStore()
const webviewStore = useWebviewStore()
const billingStore = useBillingStore()
const toastStore = useToastStore()

const loading = ref(false)

const isPricingOnboarding = computed(
  () => pricingCheckout.value?.stage === 'onboarding'
)

const buttonLabel = computed(() => {
  if (webviewStore.isActive) return 'Continue'
  if (isPricingOnboarding.value) return 'Get started'
  return 'Next'
})

const match = (
  key: OnboardingStep,
  map: [string, string][],
  fallback: string
) => {
  const value = (onboardingStore.state[key] || '') as string
  return map.find(([needle]) => value.includes(needle))?.[1] ?? fallback
}

const creations = computed(() => {
  const values = (onboardingStore.state[OnboardingStep.Two] || []) as string[]
  return values.length
    ? values.map((value) => value.toLowerCase()).join(', ')
    : 'your work'
})

const frequency = computed(() =>
  match(
    OnboardingStep.Three,
    [
      ['day', 'daily'],
      ['week', 'weekly'],
      ['month', 'monthly'],
    ],
    'a few times a year'
  )
)

const income = computed(() =>
  match(
    OnboardingStep.Four,
    [
      ['primary', 'your primary income'],
      ['Part', 'part of your income'],
      ['growing', 'a growing part of your income'],
    ],
    'not yet a source of income'
  )
)

const alerts = computed(() =>
  match(
    OnboardingStep.Seven,
    [
      ['Real-time', 'real-time alerts'],
      ['Periodic', 'periodic summaries'],
    ],
    'occasional updates'
  )
)

const protection = computed(() =>
  match(
    OnboardingStep.Nine,
    [
      ['country', 'local'],
      ['International', 'international'],
      ['Wherever', 'full'],
    ],
    'worldwide'
  )
)

const finishOnboarding = async (attempts = 3) => {
  await completeStep({ step: 11 }).catch(() => undefined)

  for (let attempt = 1; ; attempt += 1) {
    try {
      return await completeOnboarding()
    } catch (error) {
      if (attempt >= attempts) throw error

      await new Promise((settle) => setTimeout(settle, attempt * 300))
    }
  }
}

const handleClick = async () => {
  if (webviewStore.isActive) {
    await finishOnboarding()

    analytics.track('onboarding_completed', {
      trigger: 'webview_continue',
      source: webviewStore.isiOS ? 'apple' : 'google',
    })

    webviewStore.postMessage({ type: NativeActionsEnum.Basic })
    return
  }

  if (isPricingOnboarding.value) {
    const planType =
      pricingCheckout.value?.planType ?? billingStore.billing?.plan.type
    const membershipTier =
      pricingCheckout.value?.membershipTier ??
      billingStore.membershipTier ??
      MembershipTier.Pro

    loading.value = true

    try {
      await finishOnboarding()
    } catch (error) {
      toastStore.showToast({
        type: ToastType.Error,
        content: 'Something went wrong - please try again.',
        error: error as Error,
      })
      return
    } finally {
      loading.value = false
    }

    analytics.track('onboarding_completed', {
      trigger: 'pricing_checkout',
      source: 'web',
    })

    const welcomeModal =
      planType === PlanType.Pro
        ? Modals.WelcomeToDacrMembership
        : Modals.WelcomeToDacrPro

    openModal(welcomeModal, {
      ...(welcomeModal === Modals.WelcomeToDacrMembership && {
        props: { membershipTier },
      }),
    })

    await router.push('/home')
    clearPricingCheckout()
    return
  }

  completeStep({ step: 11 })
  onboardingStore.goToNextStep()
}
</script>
